/* eslint-disable @next/next/no-img-element */
'use client';

import React, { useEffect, useState, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase/client';
import Link from 'next/link';
import {
  Loader2, MapPin, Star, Flame, ChevronDown, ArrowRight,
} from 'lucide-react';
import { getDistanceKm } from '@/lib/categories';

interface DealBusiness {
  id: string;
  name: string;
  city?: string;
  category?: string;
  photos?: string[];
  rating?: number;
  reviewCount?: number;
  phone?: string;
  businessType?: 'master' | 'salon' | string;
  discount?: {
    percentage: number;
    description: string;
    originalPrice?: number;
    newPrice?: number;
    validUntil?: string;
  };
  packages?: Array<{
    name: string;
    price: number;
    discountPrice?: number;
    description?: string;
  }>;
  location?: { lat: number; lng: number };
}

interface DealItem {
  id: string;
  businessId: string;
  businessName: string;
  businessType: 'master' | 'salon';
  city?: string;
  location?: { lat: number; lng: number };
  rating: number;
  reviewCount: number;
  title: string;
  description?: string;
  originalPrice?: number;
  newPrice: number;
  discountPercentage?: number;
  photoUrl?: string;
}

function DealsContent() {
  const { t } = useTranslation('common');
  const [deals, setDeals] = useState<DealItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState('');
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const snap = await getDocs(collection(db, 'businesses'));
        const TEST_NAMES = ['מרלין', 'denis kros', 'oxy hair'];
        const allBusinesses = snap.docs.map(d => ({ id: d.id, ...d.data() })) as DealBusiness[];
        const validBusinesses = allBusinesses.filter(b => {
          const x = b as unknown as Record<string, unknown>;
          return !TEST_NAMES.some(name => b.name?.toLowerCase().includes(name)) && !x.banned && !x.frozen;
        });

        const allDeals: DealItem[] = [];

        for (const biz of validBusinesses) {
          const businessType: 'master' | 'salon' = biz.businessType === 'salon' ? 'salon' : 'master';

          // 1. Global discount on the business document
          if (biz.discount && biz.discount.percentage > 0 && biz.discount.newPrice) {
            allDeals.push({
              id: `${biz.id}-global`,
              businessId: biz.id,
              businessName: biz.name,
              businessType,
              city: biz.city,
              location: biz.location,
              rating: biz.rating || 0,
              reviewCount: biz.reviewCount || 0,
              title: biz.discount.description || `${biz.discount.percentage}% ${t('deals.discount_label')}`,
              originalPrice: biz.discount.originalPrice,
              newPrice: biz.discount.newPrice,
              discountPercentage: biz.discount.percentage,
              photoUrl: biz.photos?.[0],
            });
          }

          // 2. Inline packages (legacy schema)
          if (biz.packages) {
            biz.packages.forEach((pkg, index) => {
              if (pkg.discountPrice && pkg.discountPrice < pkg.price) {
                const discPerc = Math.round(((pkg.price - pkg.discountPrice) / pkg.price) * 100);
                allDeals.push({
                  id: `${biz.id}-pkg-${index}`,
                  businessId: biz.id,
                  businessName: biz.name,
                  businessType,
                  city: biz.city,
                  location: biz.location,
                  rating: biz.rating || 0,
                  reviewCount: biz.reviewCount || 0,
                  title: pkg.name,
                  description: pkg.description,
                  originalPrice: pkg.price,
                  newPrice: pkg.discountPrice,
                  discountPercentage: discPerc > 0 ? discPerc : undefined,
                  photoUrl: biz.photos?.[0],
                });
              }
            });
          }

          // 3. Subcollection packages (current schema)
          try {
            const pkgSnap = await getDocs(collection(db, 'businesses', biz.id, 'packages'));
            pkgSnap.docs.forEach(d => {
              const pkg = d.data() as { name?: string; description?: string; price?: number; oldPrice?: number };
              if (pkg.oldPrice && pkg.price && pkg.oldPrice > pkg.price) {
                const discPerc = Math.round(((pkg.oldPrice - pkg.price) / pkg.oldPrice) * 100);
                allDeals.push({
                  id: `${biz.id}-sub-${d.id}`,
                  businessId: biz.id,
                  businessName: biz.name,
                  businessType,
                  city: biz.city,
                  location: biz.location,
                  rating: biz.rating || 0,
                  reviewCount: biz.reviewCount || 0,
                  title: pkg.name || '',
                  description: pkg.description,
                  originalPrice: pkg.oldPrice,
                  newPrice: pkg.price,
                  discountPercentage: discPerc > 0 ? discPerc : undefined,
                  photoUrl: biz.photos?.[0],
                });
              }
            });
          } catch {
            /* subcollection might not exist */
          }
        }

        setDeals(allDeals);
      } catch (err) {
        console.error('Failed to load deals', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDeals();
  }, [t]);

  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { timeout: 5000 }
      );
    }
  }, []);

  const allCities = [...new Set(deals.map(d => d.city).filter(Boolean))].sort();

  let filtered = [...deals];
  if (city) filtered = filtered.filter(d => d.city === city);

  // Sort: highest discount first, then nearest if location is known.
  filtered.sort((a, b) => {
    const discA = a.discountPercentage || 0;
    const discB = b.discountPercentage || 0;
    if (discB !== discA) return discB - discA;
    if (userLocation && a.location && b.location) {
      return getDistanceKm(userLocation.lat, userLocation.lng, a.location.lat, a.location.lng)
        - getDistanceKm(userLocation.lat, userLocation.lng, b.location.lat, b.location.lng);
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-16 bg-background relative overflow-hidden">
      {/* Soft decorative blobs that match the rest of the app */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-[-8%] left-[-5%] w-[35vw] h-[35vw] max-w-[400px] max-h-[400px] bg-rose-500/15 blur-[100px] rounded-full"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-5%] right-[-3%] w-[30vw] h-[30vw] max-w-[350px] max-h-[350px] bg-amber-500/15 blur-[80px] rounded-full"
      />

      <div className="mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* ── Compact hero — single row on desktop, stacked on mobile ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 sm:mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-12 h-12 rounded-2xl bg-linear-to-br from-rose-500 to-amber-500 flex items-center justify-center shadow-lg shadow-rose-500/30">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-black leading-tight bg-clip-text text-transparent bg-linear-to-r from-rose-500 to-amber-500">
                {t('deals.title')}
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">
                {t('deals.description')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <div className="text-xs sm:text-sm font-bold text-muted-foreground whitespace-nowrap">
              {filtered.length > 0
                ? t('deals.found_count', { count: filtered.length, defaultValue: '{{count}} מבצעים' })
                : ''}
            </div>
            {allCities.length > 1 && (
              <div className="relative">
                <select
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  className="appearance-none bg-card border border-border rounded-full pl-9 pr-9 py-2 text-sm font-bold cursor-pointer hover:border-primary/40 transition-colors min-w-[140px]"
                >
                  <option value="">{t('catalog.all_cities', 'כל הערים')}</option>
                  {allCities.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 absolute inset-e-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                <MapPin className="w-4 h-4 absolute inset-s-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
              </div>
            )}
          </div>
        </div>

        {/* ── Deals grid (catalog-density: 2 / 3 / 4 / 5 columns) ── */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 sm:py-20 px-4 text-center bg-card/40 backdrop-blur-md rounded-3xl border border-border/50 max-w-2xl mx-auto">
            <span className="text-6xl mb-4">🔥</span>
            <h2 className="text-xl sm:text-2xl font-bold mb-2">{t('deals.empty_title')}</h2>
            <p className="text-sm sm:text-base text-muted-foreground">{t('deals.empty_desc')}</p>
          </div>
        ) : (
          <div className="space-y-4 sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 sm:gap-4 sm:space-y-0">
            {filtered.map(deal => (
              <DealCard key={deal.id} deal={deal} userLocation={userLocation} t={t} />
            ))}
          </div>
        )}

        {/* ── Promo News ── */}
        <div className="mt-12 sm:mt-16">
          <div className="flex items-center gap-3 mb-5 sm:mb-6">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-lg sm:text-xl">📰</span>
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-black truncate">{t('nav.blog')}</h2>
              <p className="text-xs text-muted-foreground truncate">{t('deals.promo_news_desc')}</p>
            </div>
          </div>
          <PromoNewsSection />
        </div>
      </div>
    </div>
  );
}

interface DealCardProps {
  deal: DealItem;
  userLocation: { lat: number; lng: number } | null;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

/**
 * Compact deal card. The whole card is a single link to the master/salon page,
 * keeping the touch target large and the layout consistent with the catalog.
 */
function DealCard({ deal, userLocation, t }: DealCardProps) {
  const dist = userLocation && deal.location
    ? getDistanceKm(userLocation.lat, userLocation.lng, deal.location.lat, deal.location.lng)
    : null;
  const href = deal.businessType === 'salon'
    ? `/salon?id=${deal.businessId}`
    : `/master?id=${deal.businessId}`;

  return (
    <Link
      href={href}
      className="group flex flex-col bg-card border border-border/60 rounded-2xl overflow-hidden hover:border-rose-500/40 hover:shadow-md hover:shadow-rose-500/10 transition-all"
    >
      {/* Image */}
      <div className="aspect-4/3 bg-secondary/50 relative overflow-hidden">
        {deal.photoUrl ? (
          <img
            src={deal.photoUrl}
            alt={deal.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl bg-linear-to-br from-rose-500/10 to-amber-500/10">
            🔥
          </div>
        )}

        {/* Gradient overlay so badges pop on bright photos */}
        <div className="absolute inset-0 bg-linear-to-t from-black/55 via-black/10 to-transparent pointer-events-none" />

        {/* Discount badge */}
        {deal.discountPercentage ? (
          <div className="absolute top-2 inset-e-2 bg-linear-to-br from-rose-500 to-rose-600 text-white px-2 py-0.5 rounded-full font-black text-xs shadow-md shadow-rose-500/30">
            −{deal.discountPercentage}%
          </div>
        ) : null}

        {/* Business name + city pinned to bottom over the gradient */}
        <div className="absolute bottom-0 inset-x-0 p-2 flex items-end justify-between gap-2 text-white drop-shadow">
          <div className="min-w-0">
            <p className="font-black text-xs truncate leading-tight">{deal.businessName}</p>
            {deal.city ? (
              <p className="text-[10px] opacity-90 flex items-center gap-1 truncate">
                <MapPin className="w-2.5 h-2.5 shrink-0" />
                <span className="truncate">{deal.city}</span>
                {dist !== null ? <span className="opacity-75">· {dist.toFixed(1)} km</span> : null}
              </p>
            ) : null}
          </div>
          {deal.rating > 0 ? (
            <div className="flex items-center gap-0.5 bg-black/40 backdrop-blur-md px-1.5 py-0.5 rounded-full text-[10px] font-black shrink-0">
              <Star className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
              {deal.rating.toFixed(1)}
            </div>
          ) : null}
        </div>
      </div>

      {/* Body */}
      <div className="p-3 flex flex-col gap-2">
        <h3 className="font-bold text-sm leading-tight line-clamp-2 group-hover:text-rose-500 transition-colors min-h-[2.5em]">
          {deal.title}
        </h3>

        <div className="flex items-baseline justify-between gap-2">
          <div className="flex items-baseline gap-1.5 min-w-0">
            {deal.originalPrice ? (
              <span className="text-[11px] text-muted-foreground line-through">
                ₪{deal.originalPrice}
              </span>
            ) : null}
            <span className="text-rose-500 font-black text-lg leading-none">
              ₪{deal.newPrice}
            </span>
          </div>
          <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-rose-500 group-hover:gap-1 transition-all whitespace-nowrap">
            {t('deals.book_now')}
            <ArrowRight className="w-3 h-3 rtl:rotate-180" />
          </span>
        </div>
      </div>
    </Link>
  );
}

// ─── Promo News ─────────────────────────────────────────────────────────────

interface PromoArticle {
  id: string;
  title: string;
  excerpt?: string;
  content: string;
  imageUrl?: string;
  authorId?: string;
  status?: string;
  articleType?: string;
}

function PromoNewsSection() {
  const { t } = useTranslation('common');
  const [articles, setArticles] = useState<PromoArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        let docs: PromoArticle[] = [];
        try {
          const snap = await getDocs(query(collection(db, 'articles'), orderBy('createdAt', 'desc'), limit(15)));
          const testWords = ['тест', 'טסט', 'בדיקה', 'test'];
          docs = snap.docs
            .map(d => ({ id: d.id, ...d.data() } as PromoArticle))
            .filter(a => (!a.status || a.status === 'approved')
              && !testWords.some(w => (a.title || '').toLowerCase().includes(w)));
        } catch (err) {
          console.error(err);
        }
        setArticles(docs.slice(0, 4));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div className="h-48 bg-card rounded-2xl animate-pulse" />
        <div className="h-48 bg-card rounded-2xl animate-pulse" />
        <div className="h-48 bg-card rounded-2xl animate-pulse hidden lg:block" />
        <div className="h-48 bg-card rounded-2xl animate-pulse hidden xl:block" />
      </div>
    );
  }
  if (articles.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {articles.map(article => (
        <Link
          key={article.id}
          href={article.authorId ? `/master?id=${article.authorId}` : '/catalog'}
          className="group bg-card rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-md hover:border-primary/30 transition-all flex flex-col"
        >
          <div className="aspect-4/3 relative overflow-hidden bg-secondary">
            {article.imageUrl ? (
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="text-4xl opacity-40 w-full h-full flex items-center justify-center">📰</div>
            )}
            {article.articleType === 'promo' ? (
              <div className="absolute top-2 inset-s-2 bg-rose-500 text-white px-2 py-0.5 rounded-full text-[10px] font-black shadow-md">
                🔥 PROMO
              </div>
            ) : null}
          </div>
          <div className="p-3 flex-1 flex flex-col gap-2">
            <h3 className="text-sm font-bold line-clamp-2 group-hover:text-primary transition-colors leading-tight">
              {article.title}
            </h3>
            <p className="text-muted-foreground text-xs line-clamp-2 flex-1 leading-relaxed">
              {article.excerpt || (article.content || '').substring(0, 100)}
            </p>
            <span className="inline-flex items-center justify-center gap-1 w-full py-2 bg-primary/10 group-hover:bg-primary text-primary group-hover:text-white rounded-lg font-bold text-center transition-all text-xs">
              {t('deals.book_now')}
              <ArrowRight className="w-3 h-3 rtl:rotate-180" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function DealsPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      }
    >
      <DealsContent />
    </Suspense>
  );
}
